import './App.css';
import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from '../../components/Header/Header';
import { AuthProvider } from '../../context/AuthContext';
import { ActiveProjectProvider } from '../../context/ActiveProjectContext';
import ProtectedRoute from '../../components/ProtectedRoute.js';
import { useAuth } from '../../hooks/useAuth';
import Menu from '../../components/Menu/Menu.js';
import Footer from '../../components/Footer/Footer';
import DataLayerTracker from '../../components/DataLayerTracker/DataLayerTracker';
import { ToastProvider } from '../../components/Notification';
import CommonPageLoader from '../../components/PageLoader';
import { routesConfig, AuthPage } from '../../config/routes';
import { canAccessRoute } from '../../utils/authPermissions';

// Компонент загрузки для Suspense
const SuspenseLoader = () => (
    <CommonPageLoader scrollOnMount={false} />
);

const HeaderWrapper = ({ onToggleSidebar, sidebarOpen }) => {
    const { isAuthenticated } = useAuth();
    return (
        <Header
            isAuthenticated={isAuthenticated}
            onToggleSidebar={onToggleSidebar}
            sidebarOpen={sidebarOpen}
        />
    );
};

const App = () => {
    return (
        <Router>
            <AuthProvider>
                <ActiveProjectProvider>
                    <ToastProvider>
                        <DataLayerTracker />
                        <AppLayout />
                    </ToastProvider>
                </ActiveProjectProvider>
            </AuthProvider>
        </Router>
    );
};

const AppLayout = () => {
    const { isAuthenticated } = useAuth();
    const location = useLocation();
    // Drawer-сайдбар на узких экранах (<1180px); на десктопе состояние не влияет
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Закрываем drawer при любой навигации
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname, location.search]);

    return (
        <div className="app-layout">
            <HeaderWrapper
                sidebarOpen={sidebarOpen}
                onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
            />
            <div className={`app-shell ${isAuthenticated ? 'authenticated' : ''}`}>
                {isAuthenticated && (
                    <>
                        <Menu open={sidebarOpen} />
                        {sidebarOpen && (
                            <div
                                className="menu-backdrop"
                                onClick={() => setSidebarOpen(false)}
                                aria-hidden="true"
                            />
                        )}
                    </>
                )}
                <div className="app-content">
                    <MainContentWrapper />
                    <Footer />
                </div>
            </div>
        </div>
    );
};

/**
 * Рендерит элемент маршрута с учётом авторизации
 */
const RouteElement = ({ route, isAuthenticated }) => {
    const { canViewReports, canViewInference, canViewCdp, isAdmin } = useAuth();
    const Component = route.component;
    
    // Публичные маршруты доступны всегда
    if (route.isPublic) {
        return <Component />;
    }
    
    // Защищённые маршруты
    if (isAuthenticated) {
        const element = route.usePathKey 
            ? <Component key={window.location.pathname} />
            : <Component />;
        const hasRouteAccess = canAccessRoute(route, {
            canViewReports,
            canViewInference,
            canViewCdp,
            isAdmin,
        });
        return <ProtectedRoute allow={hasRouteAccess}>{element}</ProtectedRoute>;
    }
    
    // Неавторизованный пользователь - показываем страницу авторизации
    return <AuthPage />;
};

const MainContentWrapper = () => {
    const { isAuthenticated } = useAuth();
    
    return (
        <div className={`main-content ${isAuthenticated ? 'authenticated' : ''}`}>
            <Suspense fallback={<SuspenseLoader />}>
                <Routes>
                    {routesConfig.map((route) => (
                        <Route
                            key={route.path}
                            path={route.path}
                            element={
                                <RouteElement 
                                    route={route} 
                                    isAuthenticated={isAuthenticated} 
                                />
                            }
                        />
                    ))}
                </Routes>
            </Suspense>
        </div>
    );
};

export default App;
